import type { ConnectivityEvent } from "@fleet/state-machines";
import type { MaintenanceScope } from "@fleet/maintenance";
import { type AuthFlowEvent, authFlowMachine } from "../state-machines/auth-flow-machine";
import { fromPromise } from "xstate";
import { type ApiClient, sharedApi } from "./api";
import { sweepVisitorData } from "./sweep-visitor-data";
import type { AbstractPowerSyncDatabase } from "@powersync/common";
import { getHasLocalDataQuery } from "@fieldcheck/powersync";
import type { Session } from "../types/auth";

/**
 * Agnostic handlers for Connectivity state management.
 */
export function createConnectivityHandlers(send: (event: ConnectivityEvent) => void) {
  return {
    onNetworkChanged: (isOnline: boolean) => {
      send({ type: "NETWORK_CHANGED", isOnline });
    },
    onAuthChanged: (isAuthenticated: boolean) => {
      send({ type: "AUTH_CHANGED", isAuthenticated });
    },
    onPowerSyncStatusChanged: (status: {
      isConnected: boolean;
      isConnecting: boolean;
      dbError: string | null;
    }) => {
      let psStatus: "connected" | "connecting" | "disconnected" | "error" = "disconnected";
      if (status.isConnected) {
        psStatus = "connected";
      } else if (status.isConnecting) {
        psStatus = "connecting";
      } else if (status.dbError) {
        psStatus = "error";
      }
      send({ type: "POWERSYNC_STATUS_CHANGED", status: psStatus });
    },
    onApiHealthChanged: (
      status: "up" | "down" | "maintenance",
      version?: string,
      message?: string,
      /**
       * Scopes of the maintenance window, if any. Carried into the machine
       * context so the UI can be PROPORTIONATE: a `writes`-only window arrives
       * with `status: "up"` (the server keeps it out of the blocking state on
       * purpose) and must show at most a discreet pill.
       */
      maintenanceScopes?: MaintenanceScope[],
    ) => {
      send({ type: "API_HEALTH_CHANGED", status, version, message, maintenanceScopes });
    },
  };
}

const HTTP_STATUS = {
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  CONFLICT: 409,
  TOO_MANY_REQUESTS: 429,
} as const;

/**
 * Stable, locale-agnostic error codes thrown by {@link getAuthFlowMachineLogic}'s
 * `verifyMfaActor`. The auth-flow machine stores a thrown actor's `error.message`
 * verbatim in `context.error`, and the login UI renders that string directly — so
 * a raw English sentence would NEVER localize. Throwing a code instead lets the
 * UI map it to a `t(msg\`…\`)` in the user's locale (see `login.tsx`'s
 * `displayError`). Covers the wrong/expired TOTP, the same for a backup code
 * (the backend rejects both with 401 on `verify-mfa`), and the 429 rate-limit.
 */
export const MFA_ERROR_CODES = {
  INVALID_CODE: "MFA_INVALID_CODE",
  RATE_LIMITED: "MFA_RATE_LIMITED",
  UNEXPECTED: "MFA_UNEXPECTED",
} as const;

/**
 * Upper bound (ms) for the guest local-data probe that gates the auth flow's
 * `checkingUnsynced` state. A stalled PowerSync read MUST NOT strand the login
 * machine forever. Mirrors the sign-out machine's `CHECK_TIMEOUT_MS` (2000ms).
 */
const AUTH_UNSYNCED_CHECK_TIMEOUT_MS = 2000;

/**
 * Reads `data.session.token` defensively from an arbitrary `data` payload
 * without using type assertions. The auth endpoints have slightly different
 * shapes (sign-in returns `AuthResponse`, sign-up returns `UserDTO`, etc.),
 * so we narrow step-by-step through the `in` operator and `typeof`.
 */
function extractSessionToken(data: unknown): string | null {
  if (typeof data !== "object" || data === null) {
    return null;
  }
  if (!("session" in data)) {
    return null;
  }
  const { session } = data;
  if (typeof session !== "object" || session === null) {
    return null;
  }
  if (!("token" in session)) {
    return null;
  }
  return typeof session.token === "string" ? session.token : null;
}

/**
 * Detects a 2FA challenge in a sign-in response and returns the challenge token
 * to replay to `verify-mfa`. A 2FA-enabled account's `sign-in/email` reply has
 * the shape `{ mfa_required: true, mfa_token: "<uuid>", session: null, ... }`
 * (serde snake_case `LoginResponse`; the generated OpenAPI types model the
 * sign-in body as the session-only `AuthResponse`, so these fields are read
 * defensively without type assertions). Returns `null` for a normal login.
 */
function extractMfaChallengeToken(data: unknown): string | null {
  if (typeof data !== "object" || data === null) {
    return null;
  }
  if (!("mfa_required" in data) || data.mfa_required !== true) {
    return null;
  }
  if (!("mfa_token" in data)) {
    return null;
  }
  return typeof data.mfa_token === "string" ? data.mfa_token : null;
}

/**
 * Reads `data.user.id` defensively from an auth response payload (sign-up and
 * sign-in both return `{ user: { id }, session: { token } }`). Used to resolve
 * the freshly-authenticated user's id WITHOUT a follow-up `get-session` call —
 * critical on the sign-up path, where the new account is not yet active in the
 * client registry, so `get-session`'s Bearer resolves to nothing and returns an
 * empty session. Deriving the id from the response is what lets the guest-data
 * sweep run at all (otherwise it is skipped and the visitor's rows never migrate).
 */
export function extractUserId(data: unknown): string | null {
  if (typeof data !== "object" || data === null || !("user" in data)) {
    return null;
  }
  const { user } = data;
  if (typeof user !== "object" || user === null || !("id" in user)) {
    return null;
  }
  return typeof user.id === "string" ? user.id : null;
}

/**
 * Helper to provide the authFlowMachine with platform-specific actors.
 */
export function getAuthFlowMachineLogic(options: {
  db: AbstractPowerSyncDatabase;
  api: ApiClient;
  visitorId: string;
  disconnectAndClear: () => Promise<void>;
  refetchQueries: (session?: Session | null) => Promise<void>;
  isWeb: boolean;
  /**
   * Optional hook called whenever a sign-in/sign-up response includes a
   * session token. Used by Tauri (where cross-site cookies fail) to persist
   * the token to `localStorage` for Bearer auth on subsequent requests.
   *
   * Receives `null` to clear (e.g. after sign-out).
   */
  persistAuthToken?: (token: string | null) => void;
  /**
   * Optional hook called after a successful sign-in/sign-up (including the
   * guest→real upgrade via the merge dialog) with the authenticated session.
   * The web app uses this to register the account in the multi-account
   * registry so it shows up in the account switcher. Receives the raw response
   * `data` (shape varies by endpoint); the callee narrows defensively.
   */
  onAccountAuthenticated?: (data: unknown) => void | Promise<void>;
  /**
   * Multi-account "add account" mode. When `true`, the web sign-in path does
   * NOT pre-sign-out the current account — the new account is ADDED to the
   * registry alongside the existing one(s) (Gmail / Microsoft To Do style)
   * instead of replacing it. Defaults to `false` (legacy single-account login,
   * which clears the current cookie/session before signing in).
   */
  isAddingAccount?: boolean;
  /**
   * Upper bound (ms) for the `checkingUnsynced` local-data probe before the auth
   * flow gives up and proceeds WITHOUT the merge prompt (degrade-to-safe).
   * Defaults to {@link AUTH_UNSYNCED_CHECK_TIMEOUT_MS}. Exposed only so unit
   * tests can shrink it; production should keep the default.
   */
  checkUnsyncedTimeoutMs?: number;
}) {
  const {
    db,
    api,
    visitorId,
    disconnectAndClear,
    refetchQueries,
    isWeb,
    persistAuthToken,
    onAccountAuthenticated,
    isAddingAccount = false,
    checkUnsyncedTimeoutMs = AUTH_UNSYNCED_CHECK_TIMEOUT_MS,
  } = options;

  /**
   * Completes a login from an authenticated response payload (the session-bearing
   * `LoginResponse` returned by BOTH a non-MFA `sign-in/email` and a successful
   * `verify-mfa`). Runs the exact same post-login work for both paths: guest data
   * sweep, Bearer-token persistence, multi-account registration and query refetch.
   */
  async function finalizeSignIn(data: unknown): Promise<void> {
    // Better Auth sign-in returns the session payload.
    // Use it directly to sweep data and sync the UI immediately. The endpoints
    // share the `LoginResponse` shape (user/session/token) but the openapi types
    // model them differently, so we assert the common session shape here (same
    // pattern the non-MFA path used before this was extracted into a helper).
    // oxlint-disable-next-line typescript/no-unsafe-type-assertion
    const sessionData = data as Session | null;
    const newUserId = sessionData?.user.id;
    if (newUserId) {
      await sweepVisitorData(db, newUserId, visitorId);
    }

    // Persist the session token (required by the Bearer auth path).
    const tokenFromResponse = extractSessionToken(data);
    if (tokenFromResponse !== null) {
      persistAuthToken?.(tokenFromResponse);
    }

    // Register the account in the multi-account registry (web switcher).
    await onAccountAuthenticated?.(data);

    await refetchQueries(sessionData);
  }

  return authFlowMachine.provide({
    actors: {
      checkUnsyncedActor: fromPromise(async () => {
        // Multi-account "add account" (mode=add): the active DB belongs to the
        // CURRENT signed-in account, not to a guest — there is nothing to
        // migrate into the account being created (the flow skips the
        // guest→real sweep too). Probing it would raise the "Keep local
        // data?" prompt over the current account's data, and answering
        // "discard" would clearDataActor the WRONG database. Skip the probe.
        if (isAddingAccount) {
          return false;
        }
        // Bound the probe so a STALLED PowerSync read can never strand the auth
        // flow in `checkingUnsynced` with the submit button stuck on
        // "Signing in…" forever. A WASQLite worker contended by a concurrent
        // multi-DB account swap, an OPFS lock, or a mid-rebuild guest DB can
        // leave this `await` pending indefinitely — and the machine's
        // `onError` fail-safe only catches REJECTIONS, never a never-settling
        // promise. On timeout we degrade to "no local data": the machine goes
        // straight to authenticating. This is SAFE — the guest→real merge runs
        // unconditionally inside `signInActor` via `sweepVisitorData`, so no
        // rows are lost; we only skip the optional "keep or discard?" prompt.
        // Mirrors the sign-out machine's existing timeout guard.
        const checkPromise = (async () => {
          try {
            const { sql, parameters } = getHasLocalDataQuery(visitorId);
            const row = await db.get<{ hasData: number }>(sql, parameters);
            return (row?.hasData ?? 0) > 0;
          } catch (error) {
            console.warn("[AuthFlow] Failed to check for unsynced changes:", error);
            return false;
          }
        })();

        const timeoutPromise = new Promise<boolean>((resolve) => {
          setTimeout(() => {
            console.warn(
              "[AuthFlow] Unsynced check timed out; proceeding without the merge prompt.",
            );
            resolve(false);
          }, checkUnsyncedTimeoutMs);
        });

        return Promise.race([checkPromise, timeoutPromise]);
      }),
      clearDataActor: fromPromise(async () => disconnectAndClear()),
      signInActor: fromPromise(async ({ input }) => {
        // On web we normally pre-sign-out so the cookie holds a single session.
        // When ADDING an account (multi-account switcher), we must NOT revoke
        // the current account — the new one is added alongside it. The web
        // build authenticates via per-account Bearer tokens, so there is no
        // cookie collision to clear in that case.
        if (isWeb && !isAddingAccount) {
          await sharedApi.signOut(api);
        }

        const { data, error, response } = await sharedApi.signIn(api, {
          email: input.email.trim(),
          password: input.password,
        });

        if (error) {
          if (response.status === HTTP_STATUS.UNAUTHORIZED) {
            throw new Error("Invalid email or password");
          }
          if (response.status === HTTP_STATUS.TOO_MANY_REQUESTS) {
            throw new Error("Too many attempts. Please wait and try again.");
          }

          throw new Error("An unexpected error occurred. Please try again.");
        }

        // 2FA-enabled account: the server returns a challenge (no session) and
        // expects the TOTP code via `verify-mfa`. Surface the challenge token so
        // the machine branches to the MFA step instead of "completing" a login
        // that has no session. Skip ALL post-login work until the code verifies.
        const mfaToken = extractMfaChallengeToken(data);
        if (mfaToken !== null) {
          return { mfaRequired: true, mfaToken };
        }

        await finalizeSignIn(data);
        return { mfaRequired: false, mfaToken: null };
      }),
      verifyMfaActor: fromPromise(async ({ input }) => {
        // The OpenAPI spec models only a 200 for `verify-mfa`, so openapi-fetch
        // types `error` as `never` (and the error branch unreachable). Branch on
        // the raw `response.status` instead to surface wrong/expired-code errors.
        const { data, response } = await api.POST("/api/auth/verify-mfa", {
          body: { requestToken: input.requestToken, otp: input.otp.trim() },
        });

        if (!response.ok) {
          // Throw STABLE codes (not English text): the login UI maps them to a
          // translated message in the user's locale. A wrong/expired TOTP AND a
          // wrong backup code both surface here as 401 → INVALID_CODE.
          if (response.status === HTTP_STATUS.UNAUTHORIZED) {
            throw new Error(MFA_ERROR_CODES.INVALID_CODE);
          }
          if (response.status === HTTP_STATUS.TOO_MANY_REQUESTS) {
            throw new Error(MFA_ERROR_CODES.RATE_LIMITED);
          }

          throw new Error(MFA_ERROR_CODES.UNEXPECTED);
        }

        // Success carries a real session (and sets the `fieldcheck.device` cookie),
        // exactly like a normal sign-in — finalize identically.
        await finalizeSignIn(data);
      }),
      signUpActor: fromPromise(async ({ input }) => {
        const { data, error, response } = await sharedApi.signUp(api, {
          name: input.name,
          email: input.email.trim(),
          password: input.password,
        });
        if (error) {
          if (response.status === HTTP_STATUS.CONFLICT) {
            throw new Error("An account with this email already exists.");
          }
          if (response.status === HTTP_STATUS.BAD_REQUEST) {
            throw new Error("Please check your name, email and password.");
          }

          throw new Error("An unexpected error occurred. Please try again.");
        }

        // Sign-up may auto-create a session. Persist the token if so.
        const tokenFromResponse = extractSessionToken(data);
        if (tokenFromResponse !== null) {
          persistAuthToken?.(tokenFromResponse);
        }

        // Resolve the new user's id from the sign-up RESPONSE first. A follow-up
        // `get-session` cannot be relied on here: the account is not yet active in
        // the client registry, so its Bearer resolves to nothing and the call
        // returns an empty session — which previously left `newUserId` undefined
        // and SKIPPED the guest-data sweep entirely (the visitor's rows kept
        // `VISITOR_ID`, and on this org-scoped backend failed the ownership check on
        // upload and were silently dropped: "Keep and merge" migrated nothing).
        // Fall back to `get-session` only if the response somehow lacks the id.
        const session = await sharedApi.getSession(api);
        const newUserId = extractUserId(data) ?? session.data?.user.id;
        // Only claim the guest's rows for a plain registration. In "add account"
        // mode the guest is a SEPARATE account that must keep its own scratch, and
        // the web layer already skips `flushGuestUpgrade` there.
        if (newUserId && !isAddingAccount) {
          await sweepVisitorData(db, newUserId, visitorId);
        }
        // Register the account in the multi-account registry (web switcher).
        // Prefer the sign-up response (it carries the opaque session token); fall
        // back to the freshly-fetched session payload.
        await onAccountAuthenticated?.(extractSessionToken(data) === null ? session.data : data);
        await refetchQueries();
      }),
    },
  });
}

/**
 * Agnostic handlers for Auth Flow (Sign In / Sign Up).
 */
export function createAuthFlowHandlers(send: (event: AuthFlowEvent) => void) {
  return {
    handleSignIn: (email: string, password: string) => {
      send({ type: "SUBMIT", email, password });
    },
    handleSignUp: (name: string, email: string, password: string) => {
      send({ type: "SUBMIT_SIGNUP", name, email, password });
    },
    handleSubmitMfa: (otp: string) => {
      send({ type: "SUBMIT_MFA", otp });
    },
    handleConfirmMerge: () => {
      send({ type: "CONFIRM_MERGE" });
    },
    handleConfirmDiscard: () => {
      send({ type: "CONFIRM_DISCARD" });
    },
    handleCancel: () => {
      send({ type: "CANCEL" });
    },
    handleRetry: () => {
      send({ type: "RETRY" });
    },
  };
}

export function evaluateSessionMonitorState(options: {
  currentSessionId: string | undefined;
  activeLocalSessions: { id: string }[] | undefined;
  sessionWasSeen: boolean;
  justChangedSession: boolean;
}) {
  const { currentSessionId, activeLocalSessions, sessionWasSeen, justChangedSession } = options;
  const isMissing = activeLocalSessions?.length === 0;

  const shouldExpire = Boolean(
    currentSessionId && isMissing && sessionWasSeen && !justChangedSession,
  );

  const isCurrentlySeen = Boolean(activeLocalSessions?.some((s) => s.id === currentSessionId));

  return {
    shouldExpire,
    isCurrentlySeen,
  };
}

export function shouldContinueSessionPolling(options: {
  hasSynced: boolean;
  currentSessionId: string | undefined;
}) {
  return !options.hasSynced && Boolean(options.currentSessionId);
}
